import { useCallback, useEffect, useState } from 'react';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  OpenInNew,
  ContentCopy,
  Description,
  Delete,
  ContentCopy as CopyIcon,
  Business,
  CallSplit,
  PersonRemove,
} from '@mui/icons-material';
import {
  Badge,
  Button,
  IconButton,
  Tooltip,
  Box,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { useGetProviderInfoQuery } from '../store/api';
import {
  useGetAdminProviderTemplatesQuery,
  useDeleteAdminNoteTemplateMutation,
  useRemoveUserTemplateMutation,
  useForkTemplateForUserMutation,
  type EffectiveNoteTemplateSummary,
} from '../store/hooks';
import { formatDateTime } from '../utils/formatting';
import { CopyTemplateDialog } from '../components/CopyTemplateDialog';
import { ConvertToOrgDialog } from '../components/ConvertToOrgDialog';

export default function TemplatesListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const perPageParam = searchParams.get('perPage');
    return perPageParam ? parseInt(perPageParam, 10) : 20;
  });
  const [scopeFilter, setScopeFilter] = useState<'all' | 'public' | 'org' | 'user'>(() => {
    const scopeParam = searchParams.get('scope');
    return (scopeParam as 'all' | 'public' | 'org' | 'user') || 'all';
  });
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EffectiveNoteTemplateSummary | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EffectiveNoteTemplateSummary | null>(null);
  const [deleteAction, setDeleteAction] = useState<'remove' | 'delete'>('delete');
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  const [selectedTemplateToFork, setSelectedTemplateToFork] = useState<EffectiveNoteTemplateSummary | null>(null);
  const [bypassForkRestriction, setBypassForkRestriction] = useState(false);

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Add page if not 1
    if (page !== 1) {
      newParams.set('page', page.toString());
    }

    // Add perPage if not 20
    if (perPage !== 20) {
      newParams.set('perPage', perPage.toString());
    }

    // Add scope filter if not 'all'
    if (scopeFilter !== 'all') {
      newParams.set('scope', scopeFilter);
    }

    setSearchParams(newParams, { replace: true });
  }, [page, perPage, scopeFilter, setSearchParams]);

  // Get URL for each row
  const getRowUrl = useCallback(
    (template: EffectiveNoteTemplateSummary) => {
      return `/admin-v2/provider/${encodeURIComponent(providerId!)}/templates/${template.template.id}`;
    },
    [providerId],
  );

  // Fetch provider info
  const { data: provider, isLoading: userLoading } = useGetProviderInfoQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  // Fetch templates for the provider using new API (what provider actually sees)
  const { data: templatesData, isLoading: templatesLoading } = useGetAdminProviderTemplatesQuery(
    {
      providerId: providerId ?? '',
      organizationId: provider?.organization_id || '',
    },
    { skip: !providerId || !provider?.organization_id },
  );

  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteAdminNoteTemplateMutation();
  const [removeUserTemplate, { isLoading: isRemoving }] = useRemoveUserTemplateMutation();
  const [forkTemplate, { isLoading: isForking }] = useForkTemplateForUserMutation();

  // Filter templates by scope
  const filteredTemplates = templatesData?.templates?.filter(template => {
    if (scopeFilter === 'all') return true;
    return template.template.owner_scope === scopeFilter;
  }) || [];

  // Pagination (client-side for now, API doesn't support pagination yet)
  const paginatedTemplates = filteredTemplates.slice((page - 1) * perPage, page * perPage);
  const totalTemplates = filteredTemplates.length;

  const handleOpenDeleteDialog = useCallback((template: EffectiveNoteTemplateSummary, action: 'remove' | 'delete', e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setTemplateToDelete(template);
    setDeleteAction(action);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!templateToDelete) return;
    const templateId = templateToDelete.template.id;
    try {
      if (deleteAction === 'delete') {
        await deleteTemplate({ templateId }).unwrap();
        enqueueSnackbar('Template permanently deleted', { variant: 'success' });
      } else {
        if (!providerId) return;
        await removeUserTemplate({ userId: providerId, templateId }).unwrap();
        enqueueSnackbar('Template removed from user', { variant: 'success' });
      }
      handleCloseDeleteDialog();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to delete template', { variant: 'error' });
    }
  }, [templateToDelete, deleteAction, deleteTemplate, removeUserTemplate, providerId, enqueueSnackbar, handleCloseDeleteDialog]);

  const handleCopyClick = useCallback((template: EffectiveNoteTemplateSummary) => {
    setSelectedTemplate(template);
    setCopyDialogOpen(true);
  }, []);

  const handleConvertClick = useCallback((template: EffectiveNoteTemplateSummary) => {
    setSelectedTemplate(template);
    setConvertDialogOpen(true);
  }, []);

  const handleOpenForkDialog = useCallback((template: EffectiveNoteTemplateSummary, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedTemplateToFork(template);
    setBypassForkRestriction(false);
    setForkDialogOpen(true);
  }, []);

  const handleCloseForkDialog = useCallback(() => {
    setForkDialogOpen(false);
    setSelectedTemplateToFork(null);
    setBypassForkRestriction(false);
  }, []);

  const handleForkTemplate = useCallback(async () => {
    if (!selectedTemplateToFork || !providerId || !provider) return;

    try {
      const result = await forkTemplate({
        adminForkTemplateForUserRequest: {
          source_template_id: selectedTemplateToFork.template?.id || '',
          target_user_id: providerId,
          target_organization_id: provider.organization_id || '',
          bypass_fork_restriction: bypassForkRestriction || undefined,
        },
      }).unwrap();

      handleCloseForkDialog();

      if (result.already_existed) {
        enqueueSnackbar('User already has a customized version of this template', {
          variant: 'info',
        });
      } else {
        enqueueSnackbar('Template forked successfully', { variant: 'success' });
      }

      // Navigate to the forked template editor
      if (result.template?.id) {
        navigate(`/admin-v2/templates/${result.template.id}`);
      }
    } catch (error: any) {
      if (error?.status === 403) {
        enqueueSnackbar(
          "This template doesn't allow user forks. Enable 'Force fork' to override.",
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(
          error?.data?.message || 'Failed to fork template',
          { variant: 'error' },
        );
      }
    }
  }, [selectedTemplateToFork, providerId, provider, bypassForkRestriction, forkTemplate, navigate, enqueueSnackbar, handleCloseForkDialog]);

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const templatesColumns = [
    {
      key: 'title',
      label: 'Template Name',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm font-medium text-gray-900">
          {row.revision?.title || 'Unnamed Template'}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Template ID',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const templateId = row.template?.id || '';
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-mono">
              {templateId ? (templateId.length > 20 ? templateId.substring(0, 20) + '...' : templateId) : 'N/A'}
            </span>
            {templateId && (
              <Tooltip title="Copy ID">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    copy(templateId, 'Template ID');
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      key: 'owner_scope',
      label: 'Scope',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const scope = row.template?.owner_scope || 'user';
        const scopeColors: Record<string, 'primary' | 'secondary' | 'success' | 'default'> = {
          public: 'primary',
          org: 'success',
          user: 'secondary',
        };
        return (
          <Chip
            label={scope}
            size="small"
            color={scopeColors[scope] || 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      key: 'processing_version',
      label: 'Processing',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const version = row.template?.note_processing_version ?? 2;
        return (
          <Chip
            label={`V${version}`}
            size="small"
            color={version === 2 ? 'info' : 'default'}
            variant={version === 2 ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      key: 'version',
      label: 'Version',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm text-gray-600">v{row.revision?.version || row.template?.current_version || 1}</span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm text-gray-600">
          {row.template?.created_at ? formatDateTime(row.template.created_at) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Modified',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm text-gray-600">
          {row.template?.updated_at ? formatDateTime(row.template.updated_at) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const templateId = row.template?.id || '';
        const scope = row.template?.owner_scope || 'user';
        const isPublicOrOrg = scope === 'public' || scope === 'org';
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isPublicOrOrg && (
              <Tooltip title="Fork for User">
                <IconButton
                  size="small"
                  onClick={e => handleOpenForkDialog(row, e)}
                  color="primary"
                >
                  <CallSplit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {scope === 'user' && (
              <Tooltip title="Convert to Organization Template">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleConvertClick(row);
                  }}
                  color="warning"
                >
                  <Business fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Copy Template">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleCopyClick(row);
                }}
                color="primary"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {scope === 'org' ? (
              <Tooltip title="Org templates can only be managed from the organization page">
                <span>
                  <IconButton size="small" disabled>
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : scope === 'public' ? (
              <Tooltip title="Remove from this user's list">
                <IconButton
                  size="small"
                  onClick={e => handleOpenDeleteDialog(row, 'remove', e)}
                  color="warning"
                >
                  <PersonRemove fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Permanently delete this template">
                <IconButton
                  size="small"
                  onClick={e => handleOpenDeleteDialog(row, 'delete', e)}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {provider.name}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Templates</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Provider Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
                  <p className="text-gray-600">All templates for {provider.name}</p>
                  <p className="text-sm text-gray-500">Provider ID: {provider.provider_id}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Templates</div>
                <div className="text-2xl font-bold text-gray-900">{totalTemplates}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scope Filter Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={scopeFilter}
            onChange={(_, newValue) => {
              setScopeFilter(newValue);
              setPage(1);
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="All" value="all" />
            <Tab label="Public" value="public" />
            <Tab label="Organization" value="org" />
            <Tab label="User" value="user" />
          </Tabs>
        </Box>

        {/* Filterable Data Table */}
        <FilterableDataTable
          title="Provider Templates"
          columns={templatesColumns as any}
          data={paginatedTemplates}
          total={totalTemplates}
          loading={templatesLoading}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          getRowUrl={getRowUrl}
          emptyMessage="No templates found for this provider"
        />

        {/* Dialogs */}
        <CopyTemplateDialog
          open={copyDialogOpen}
          onClose={() => {
            setCopyDialogOpen(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
        <ConvertToOrgDialog
          open={convertDialogOpen}
          onClose={() => {
            setConvertDialogOpen(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
          defaultOrganizationId={provider?.organization_id}
          defaultOrganizationName={provider?.organization_name}
        />

        {/* Delete/Remove Template Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
         maxWidth={"xs"}>
          <DialogTitle>
            {deleteAction === 'delete' ? 'Permanently Delete Template' : 'Remove Template from User'}
          </DialogTitle>
          <DialogContent>
            {deleteAction === 'delete' ? (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  This action cannot be undone.
                </Alert>
                <Typography>
                  This will permanently delete{' '}
                  <strong>{templateToDelete?.revision?.title || 'this template'}</strong>.
                  It will no longer be available to{' '}
                  <strong>{provider?.name || providerId}</strong> or anyone else.
                </Typography>
              </>
            ) : (
              <Typography>
                This will remove{' '}
                <strong>{templateToDelete?.revision?.title || 'this template'}</strong>{' '}
                from <strong>{provider?.name || providerId}</strong>&apos;s template list.
                The template will still be available to other users.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} disabled={isDeleting || isRemoving}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={isDeleting || isRemoving}
              startIcon={(isDeleting || isRemoving) ? <CircularProgress size={16} /> : undefined}
            >
              {(isDeleting || isRemoving)
                ? (deleteAction === 'delete' ? 'Deleting...' : 'Removing...')
                : (deleteAction === 'delete' ? 'Delete Forever' : 'Remove')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Fork Template Dialog */}
        <Dialog
          open={forkDialogOpen}
          onClose={handleCloseForkDialog}
          fullWidth
         maxWidth={"sm"}>
          <DialogTitle>Fork Template for User</DialogTitle>
          <DialogContent>
            {selectedTemplateToFork && (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Create a personalized copy of this template for{' '}
                  <strong>{provider?.name || providerId}</strong>.
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Template:</strong>{' '}
                    {selectedTemplateToFork.revision?.title || 'Unnamed Template'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Type:</strong> {selectedTemplateToFork.template?.owner_scope || 'user'}
                  </Typography>
                </Alert>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bypassForkRestriction}
                      onChange={e => setBypassForkRestriction(e.target.checked)}
                    />
                  }
                  label="Force fork even if template doesn't allow forks"
                />
                <Typography variant="caption" color="text.secondary" sx={{display: "block",  ml: 4, mt: -1 }}>
                  Enable this if the template has fork restrictions but you need to create a copy anyway.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForkDialog} disabled={isForking}>
              Cancel
            </Button>
            <Button
              onClick={handleForkTemplate}
              disabled={isForking || !selectedTemplateToFork}
              variant="contained"
              color="primary"
              startIcon={isForking ? <CircularProgress size={16} color="inherit" /> : <CallSplit />}
            >
              {isForking ? 'Forking...' : 'Confirm Fork'}
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
}
